import File from "./module/common/models/File.ts"
import Invoice from "./module/common/models/Invoice.ts"
import { defineStore } from 'pinia'
import Uuid from "./module/common/models/Uuid.ts"
import { merge_invoice_pdfs, stamp } from "./module/pdf/module.ts"
import Error from "./module/error_handler/types/Error.ts"
import { ErrorClass, ErrorZone } from "./module/error_handler/types/enums.ts"
import InvoiceStatus from "./module/common/models/InvoiceStatus.ts"
import { get_project_or_purchase_order, ProjectOrPurchaseOrder } from "./module/acumatica/module.ts"
import { UserAuthStatus } from "./module/auth/types.ts"

type State = {
    _is_acumatica_authed: boolean
    _projects_sync_succeeded_at: string
    _projects_sync_attempted_at: string
    _purchase_orders_sync_succeeded_at: string
    _purchase_orders_sync_attempted_at: string
    _files: File[]
    _invoice_statuses: { [key: string]: InvoiceStatus }
    _selected_file_id: Uuid|null
    _selected_invoice_ids: Uuid[]
    _errors: Error[]
}

export const store = defineStore('store', {
    state: (): State => ({
        _is_acumatica_authed: true,
        _projects_sync_succeeded_at: "",
        _projects_sync_attempted_at: "",
        _purchase_orders_sync_succeeded_at: "",
        _purchase_orders_sync_attempted_at: "",
        _files: [],
        _invoice_statuses: {},
        _selected_file_id: null,
        _selected_invoice_ids: [],
        _errors: []
    }),
    getters: {
        is_acumatica_authed(state): boolean {
            return state._is_acumatica_authed
        },
        projects_sync_succeeded_at(state): string {
            return state._projects_sync_succeeded_at
        },
        projects_sync_attempted_at(state): string {
            return state._projects_sync_attempted_at
        },
        purchase_orders_sync_succeeded_at(state): string {
            return state._purchase_orders_sync_succeeded_at
        },
        purchase_orders_sync_attempted_at(state): string {
            return state._purchase_orders_sync_attempted_at
        },
        files(state): File[] {
            return state._files
        },
        invoice_statuses(state): { [key: string]: InvoiceStatus } {
            return state._invoice_statuses
        },
        selected_file_id(state) {
            return state._selected_file_id
        },
        selected_invoice_ids(state) {
            return state._selected_invoice_ids
        },
        file(state) {
            const files = state._files
            const selected_file_id = state._selected_file_id

            if (selected_file_id === null) {
                return null
            }

            const rtn_file = files.find((file) => {
                return file.id.get() === selected_file_id.get()
            })

            if (typeof rtn_file === "undefined") {
                return null
            } else {
                return rtn_file
            }
        },
        selected_invoices(state) {
            const selected_invoice_ids = state._selected_invoice_ids
            const selected_file_id = state._selected_file_id
            const files = state._files

            let file

            if (selected_file_id === null) {
                return []
            }

            const rtn_file = files.find((tmp_file) => {
                return tmp_file.id.get() === selected_file_id.get()
            })

            if (typeof rtn_file === "undefined") {
                file = null
            } else {
                file = rtn_file
            }
            
            const selected_invoice_ids_string = selected_invoice_ids.map((tmp_invoice_id) => {
                return tmp_invoice_id.get()
            })

            if (file === null || selected_invoice_ids.length <= 0) {
                return []
            } else {
                return file.invoices.filter((tmp_invoice) => {
                    return selected_invoice_ids_string.includes(tmp_invoice.id.get())
                })
            }
        },
        errors(state): Error[] {
            return state._errors
        },
    },
    actions: {
        set_accumatica_authed(is_authed: boolean) {
            this._is_acumatica_authed = is_authed
        },
        set_user_status(arg_user_status: UserAuthStatus) {
            this._is_acumatica_authed = arg_user_status.is_acumatica_authed
            this._projects_sync_succeeded_at = arg_user_status.projects_sync_succeeded_at
            this._projects_sync_attempted_at = arg_user_status.projects_sync_attempted_at
            this._purchase_orders_sync_succeeded_at = arg_user_status.purchase_orders_sync_succeeded_at
            this._purchase_orders_sync_attempted_at = arg_user_status.purchase_orders_sync_attempted_at
        },
        update_file(file: File) {
            const file_index = this._files.findIndex((tmp_file: File) => tmp_file.id.get() == file.id.get())
            this._files.splice(file_index, 1, file)
        },
        add_file(id: Uuid, file_name: string, file_type: string, file_size: number, file_last_modified: string) {
            this._files.unshift(new File(id, file_name, file_type, file_size, file_last_modified))
            // this._files.push()
        },
        delete_file(file: File) {
            const index = this._files.findIndex((tmp_file) => tmp_file.id == file.id)
            this._files.splice(index, 1)
        },
        clear_all() {
            this._selected_file_id = null,
            this._selected_invoice_ids = [],
            this._files = []

        },
        clear_and_select_invoices(file: File, invoices: Invoice[]) {
            this._selected_file_id = file.id

            this._selected_invoice_ids = invoices.map((tmp_invoice) => {
                return tmp_invoice.id
            })
        },
        select_additional_invoices(file: File, invoices: Invoice[]) {
            const currently_selected_ids = this._selected_invoice_ids.map((tmp_invoice_id) => {
                return tmp_invoice_id.get()
            })

            const new_invoice_ids = invoices.map((tmp_invoice) => {
                return tmp_invoice.id.get()
            })

            const invoice_ids_to_select = currently_selected_ids.concat(new_invoice_ids)

            this._selected_file_id = file.id

            // The purpose of this is to preserve the order of the invoice ids even if they are selected out of order
            this._selected_invoice_ids = file.invoices
                .filter((tmp_invoice) => {
                    return invoice_ids_to_select.includes(tmp_invoice.id.get())
                })
                .map((tmp_invoice) => {
                    return tmp_invoice.id
                })
        },
        select_file_only(file: File) {
            this._selected_invoice_ids = []
            this._selected_file_id = file.id
        },
        merge_invoices(file: File, invoice_ids_to_merge: Uuid[]): void{
            if (invoice_ids_to_merge.length <= 0) return;
    
            const invoice_id_strings_to_merge = invoice_ids_to_merge.map((tmp_invoice_id) => {
                return tmp_invoice_id.get()
            })
    
            const merged_invoice_id = invoice_ids_to_merge[0];
            const invoices_to_merge = file.invoices.filter((tmp_invoice) =>
                invoice_id_strings_to_merge.includes(tmp_invoice.id.get())
            );
    
            file.invoices = file.invoices.map((tmp_invoice) => {
                if (invoice_id_strings_to_merge.includes(tmp_invoice.id.get())) {
                    return { ...tmp_invoice, loading: true };
                } else {
                    return tmp_invoice;
                }
            });

            this.update_file(file)
    
            const merged_invoice = new Invoice();
            merged_invoice.id = merged_invoice_id
            this._selected_invoice_ids = []
            merge_invoice_pdfs(invoices_to_merge, false)
                .then((merged_pdf_bytes) => {
                    merged_invoice.pdf_bytes = merged_pdf_bytes;
    
                    file.invoices = file.invoices
                        .map((tmp_invoice) => {
                            if (tmp_invoice.id.get() === merged_invoice_id.get()) {
                                return merged_invoice;
                            } else if (invoice_id_strings_to_merge.includes(tmp_invoice.id.get())) {
                                return null;
                            } else {
                                return tmp_invoice;
                            }
                        })
                        .filter((tmp_invoice): tmp_invoice is Invoice => tmp_invoice !== null);
                })
                .catch((error) => {
                    // TODO: Throw proper error or handle it meaningfully
                    console.error("Error merging invoices:", error);
                })
                .finally(() => {
                    file.invoices = file.invoices.map((tmp_invoice) => {
                        if (invoice_id_strings_to_merge.includes(tmp_invoice.id.get())) {
                            return { ...tmp_invoice, loading: false };
                        } else {
                            return tmp_invoice;
                        }
                    });

                    this.update_file(file)
                    this._selected_invoice_ids = [merged_invoice_id]
                });
        },
        log_error(arg_error: Error) {
            this._errors.push(arg_error)
        },
        update_invoice_and_stamp: async function(arg_file: File, arg_invoice: Invoice) {
            if (arg_invoice.stamped && arg_invoice.pdf_bytes !== null) {
                try {
                    const stamped_pdf_bytes = await stamp(arg_invoice.stamp_x, arg_invoice.stamp_y, arg_invoice.invoice_information, arg_invoice.pdf_bytes)
                    arg_invoice.stamped_pdf_bytes = stamped_pdf_bytes
                } catch (original_error) {
                    if ("id" in original_error
                        && "error_message" in original_error
                        && "error_class" in original_error
                    ) {
                        const error: Error = original_error
                        error.error_zone = ErrorZone.pdf_viewer

                        this.log_error(error)
                    } else {
                        throw original_error
                    }
                }
            }
            arg_file.update_invoice(arg_invoice)
            this.update_file(arg_file)
        },
        fetch_project_data: async function(arg_file_id: Uuid, arg_invoice_id: Uuid, arg_project_or_purchase_order_NaN: string) {
            const loc_project_or_purchase_order_NaN_matches = arg_project_or_purchase_order_NaN.match(/\b\d{6}\b/g);

            let loc_files = this._files
            const get_file_and_invoice = function (arg_file_id: Uuid, arg_invoice_id: Uuid) {
                const loc_file = loc_files.find((tmp_file: File) => {
                    return tmp_file.id.get() === arg_file_id.get()
                })

                const loc_invoice: Invoice = loc_file.invoices.find((tmp_invoice: File) => {
                    return tmp_invoice.id.get() === arg_invoice_id.get()
                })

                return [loc_file, loc_invoice]
            }

            if (loc_project_or_purchase_order_NaN_matches !== null 
                && Array.isArray(loc_project_or_purchase_order_NaN_matches)
                && loc_project_or_purchase_order_NaN_matches.length > 0
            ) {
                let [loc_file, loc_invoice] = get_file_and_invoice(arg_file_id, arg_invoice_id)

                loc_invoice.loading = true
                loc_invoice.error = false
                loc_invoice.downloaded = false
                loc_invoice.not_found = false
                loc_file.update_invoice(loc_invoice)
                this.update_file(loc_file)

                let loc_project_or_purchase_order: ProjectOrPurchaseOrder|null = null
                try {
                    loc_project_or_purchase_order = await get_project_or_purchase_order(loc_project_or_purchase_order_NaN_matches[0]) as ProjectOrPurchaseOrder
                } catch (e) {
                    const error = {
                        id: new Uuid(),
                        original_error: e,
                        error_message: "Unknown error fetching project",
                        error_class: ErrorClass.user,
                        error_zone: ErrorZone.application,
                        thrown_error: null,
                        extra_data: {arg_file_id, arg_invoice_id, arg_project_or_purchase_order_NaN}
                    } as Error
                    
                    this.log_error(error)

                    let [loc_file, loc_invoice] = get_file_and_invoice(arg_file_id, arg_invoice_id)

                    loc_invoice.loading = false
                    loc_invoice.error = true
                    loc_invoice.downloaded = false
                    loc_invoice.not_found = false
                    loc_file.update_invoice(loc_invoice)
                    this.update_file(loc_file)
                    return
                }

                if (loc_project_or_purchase_order.response_status === 404) {
                    let [loc_file, loc_invoice] = get_file_and_invoice(arg_file_id, arg_invoice_id)

                    loc_invoice.loading = false
                    loc_invoice.error = false
                    loc_invoice.downloaded = false
                    loc_invoice.not_found = true
                    loc_file.update_invoice(loc_invoice)
                    this.update_file(loc_file)
                    return
                }

                if (loc_project_or_purchase_order.response_status === 401) {
                    let [loc_file, loc_invoice] = get_file_and_invoice(arg_file_id, arg_invoice_id)

                    loc_invoice.loading = false
                    loc_invoice.error = false
                    loc_invoice.downloaded = false
                    loc_invoice.not_found = false
                    loc_invoice.paused = true
                    loc_file.update_invoice(loc_invoice)
                    this.update_file(loc_file)
                    return
                }

                if (loc_project_or_purchase_order.response_status === 500) {                 
                    const error = {
                        id: new Uuid(),
                        error_message: "Unable to fetch data for invoice from acumatica",
                        error_class: ErrorClass.user,
                        error_zone: ErrorZone.application,
                        thrown_error: null,
                        extra_data: {arg_file_id, arg_invoice_id, arg_project_or_purchase_order_NaN}
                    } as Error
                    
                    this.log_error(error)

                    let [loc_file, loc_invoice] = get_file_and_invoice(arg_file_id, arg_invoice_id)

                    loc_invoice.loading = false
                    loc_invoice.error = true
                    loc_invoice.downloaded = false
                    loc_invoice.not_found = false
                    loc_file.update_invoice(loc_invoice)
                    this.update_file(loc_file)
                    return
                }

                if (loc_project_or_purchase_order) {
                    let [loc_file, loc_invoice] = get_file_and_invoice(arg_file_id, arg_invoice_id)
                    
                    loc_invoice.loading = false
                    loc_invoice.error = false
                    loc_invoice.downloaded = true
                    loc_invoice.not_found = false
                    loc_invoice.invoice_information.project_description = loc_project_or_purchase_order.description
                    loc_invoice.invoice_information.project_manager = loc_project_or_purchase_order.projectManager
                    loc_invoice.invoice_information.customer = loc_project_or_purchase_order.customer
                    loc_invoice.purchase_order_line_items = loc_project_or_purchase_order.lineItems

                    // loc_file.update_invoice(loc_invoice)
                    await this.update_invoice_and_stamp(loc_file, loc_invoice)
                } else {
                    let [loc_file, loc_invoice] = get_file_and_invoice(arg_file_id, arg_invoice_id)

                    loc_invoice.loading = false
                    loc_invoice.error = true
                    loc_invoice.downloaded = false
                    loc_invoice.not_found = false
                    loc_file.update_invoice(loc_invoice)
                    this.update_file(loc_file)
                }
            }
        },
        check_for_duplicates() {
            const loc_invoice_NaNs_seen: Set<string> = new Set();
            const loc_invoice_NaNs_duplicates: Set<string> = new Set();
            const invoice_NaN_references = {}

            const loc_files: File[] = this.files

            for (const tmp_file of loc_files) {
                for (const tmp_invoice of tmp_file.invoices) {
                    if (tmp_invoice.duplicated === true) {
                        tmp_invoice.duplicated = false
                        tmp_file.update_invoice(tmp_invoice)
                        this.update_file(tmp_file)
                    }

                    const tmp_invoice_NaN = tmp_invoice.invoice_information.invoice_NaN.trim()
                    const tmp_invoice_vendor = tmp_invoice.invoice_information.vendor.trim()
                    const tmp_duplicate_lookup_key = tmp_invoice_vendor + "___" + tmp_invoice_NaN

                    if (tmp_invoice_NaN != "" && !tmp_invoice.excluded) {
                        if (!Object.hasOwn(invoice_NaN_references, tmp_duplicate_lookup_key)) {
                            invoice_NaN_references[tmp_duplicate_lookup_key] = []
                        }

                        invoice_NaN_references[tmp_duplicate_lookup_key].push({
                            "file_id": tmp_file.id.get(),
                            "invoice_id": tmp_invoice.id.get()
                        })

                        if (loc_invoice_NaNs_seen.has(tmp_duplicate_lookup_key)) {
                            loc_invoice_NaNs_duplicates.add(tmp_duplicate_lookup_key);
                        } else {
                            loc_invoice_NaNs_seen.add(tmp_duplicate_lookup_key);
                        }

                    }
                }
            }

            for (const tmp_duplicate of loc_invoice_NaNs_duplicates) {
                for (const tmp_invoice_NaN_reference of invoice_NaN_references[tmp_duplicate]) {
                    let loc_file = this.files.find((tmp_file) => {
                        return tmp_file.id.get() == tmp_invoice_NaN_reference.file_id
                    })

                    if (!loc_file) {
                        return;
                    }

                    let loc_invoice = loc_file.invoices.find((tmp_invoice) => {
                        return tmp_invoice.id.get() == tmp_invoice_NaN_reference.invoice_id
                    })

                    if (!loc_invoice) {
                        return;
                    }

                    loc_invoice.duplicated = true
                    loc_file.update_invoice(loc_invoice)
                    this.update_file(loc_file)
                }
            }
        }
    },
})


export type MainStore = ReturnType<typeof store>